package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.domain.model.CostCategoryCost
import br.com.vvrefeicoes.portal.domain.model.CostsSnapshot
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.MonthlyCost
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryCatalog
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.CostsRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteCostsRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : CostsRepository {
    override suspend fun load(
        selection: PeriodSelection,
        unitId: String?,
        manager: String?,
        director: String?,
    ): CostsSnapshot {
        val path = "/operational/costs?${panelQuery(selection, unitId, manager, director)}"
        val response = httpClient.get(baseUrl + path) { authorize() }
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<PanelApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response.body<CostsDto>().toDomain()
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
private data class CostsDto(
    val period: PanelMonthDto = PanelMonthDto(),
    val totalCents: Long = 0,
    val fopagCents: Long = 0,
    val operationalCents: Long = 0,
    val revenueCents: Long = 0,
    val headcount: Int = 0,
    val monthly: List<MonthlyCostDto> = emptyList(),
    val months: List<PanelMonthDto> = emptyList(),
    val categories: List<CostCategoryDto> = emptyList(),
)

@Serializable
private data class MonthlyCostDto(
    val period: PanelMonthDto = PanelMonthDto(),
    val costCents: Long = 0,
    val revenueCents: Long = 0,
)

@Serializable
private data class CostCategoryDto(
    val categoryId: String = "",
    val monthlyCents: List<Long> = emptyList(),
)

private fun CostsDto.toDomain(): CostsSnapshot =
    CostsSnapshot(
        period = MonthPeriod(period.year, period.month),
        totalCents = totalCents,
        fopagCents = fopagCents,
        operationalCents = operationalCents,
        revenueCents = revenueCents,
        headcount = headcount,
        monthly = monthly.map { MonthlyCost(MonthPeriod(it.period.year, it.period.month), it.costCents, it.revenueCents) },
        months = months.map { MonthPeriod(it.year, it.month) },
        categories =
            categories.mapNotNull { category ->
                val title = OperationalEntryCatalog.expenseTables.firstOrNull { it.id == category.categoryId }?.title
                title?.let { CostCategoryCost(categoryId = category.categoryId, title = it, monthlyCents = category.monthlyCents) }
            },
    )
